import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Tooltip,
} from "@mui/material";
import {
  SwapHoriz as TransferIcon,
  TrendingUp as PromotionIcon,
  TrendingDown as RelegationIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import { apiRequest } from "../config/api";
import { Player, Division } from "../types";
import { useSeason } from "../contexts/SeasonContext";
import SeasonSelector from "../components/SeasonSelector";

interface Transfer {
  playerId: string;
  playerName: string;
  playerProfilePicture?: string;
  fromDivision: Division;
  toDivision: Division;
  movedAt: Date;
  movedBy?: string;
  reason?: string;
  seasonId?: string;
}

function Transfers() {
  const { selectedSeason } = useSeason();
  const [transfers, setTransfers] = useState<Transfer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchTransfers();
  }, [selectedSeason]);

  const fetchTransfers = async () => {
    if (!selectedSeason) return;

    try {
      setLoading(true);
      const params = new URLSearchParams({
        seasonId: selectedSeason.id,
      });

      const response = await apiRequest(`/api/transfers?${params.toString()}`);
      if (response.ok) {
        const transfersData = await response.json();

        // Sort by date (most recent first)
        const sortedTransfers = transfersData
          .map((t: any) => ({
            ...t,
            movedAt: new Date(t.movedAt),
          }))
          .sort(
            (a: Transfer, b: Transfer) =>
              b.movedAt.getTime() - a.movedAt.getTime(),
          );

        setTransfers(sortedTransfers);
      }
    } catch (error) {
      console.error("Error fetching transfers:", error);
      setError("Failed to fetch transfer history");
    } finally {
      setLoading(false);
    }
  };

  const getTransferType = (
    from: Division,
    to: Division,
  ): "promotion" | "relegation" | "lateral" => {
    const divisionRanks: { [key in Division]: number } = {
      Master: 1,
      "Division 1": 2,
      "Division 2": 3,
      "Division 3": 4,
      "Women Division": 1,
      "Women Division 2": 2,
      Test: 99,
      Open: 1, // Padel Eagle highest level
      Advanced: 2, // Padel Eagle advanced level
      Intermediate: 3, // Padel Eagle intermediate level
      Beginner: 4, // Padel Eagle beginner level
      All: 0, // Special case - not used for transfers
      unassigned: 100, // Unassigned players - not yet in ranking system
    };

    const fromRank = divisionRanks[from];
    const toRank = divisionRanks[to];

    if (fromRank > toRank) return "promotion";
    if (fromRank < toRank) return "relegation";
    return "lateral";
  };

  const getTransferIcon = (from: Division, to: Division) => {
    const type = getTransferType(from, to);
    switch (type) {
      case "promotion":
        return <PromotionIcon sx={{ color: "#4caf50" }} />;
      case "relegation":
        return <RelegationIcon sx={{ color: "#f44336" }} />;
      default:
        return <TransferIcon sx={{ color: "#2196f3" }} />;
    }
  };

  const getTransferColor = (from: Division, to: Division) => {
    const type = getTransferType(from, to);
    switch (type) {
      case "promotion":
        return "#e8f5e9";
      case "relegation":
        return "#ffebee";
      default:
        return "#e3f2fd";
    }
  };

  if (loading) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>
          Player Transfers
        </Typography>
        <Typography>Loading transfer history...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <TransferIcon sx={{ fontSize: 40, color: "#1976d2" }} />
          <Box>
            <Typography variant="h4">Player Transfers</Typography>
            <Typography variant="body2" color="text.secondary">
              Complete history of all division transfers
            </Typography>
          </Box>
        </Box>
        <SeasonSelector fullWidth={false} excludeDrafts={true} />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="h6">
              Total Transfers: {transfers.length}
            </Typography>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <PromotionIcon sx={{ fontSize: 18, color: "#4caf50" }} />
                <Typography variant="caption" color="text.secondary">
                  Promotion
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <RelegationIcon sx={{ fontSize: 18, color: "#f44336" }} />
                <Typography variant="caption" color="text.secondary">
                  Relegation
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <TransferIcon sx={{ fontSize: 18, color: "#2196f3" }} />
                <Typography variant="caption" color="text.secondary">
                  Lateral
                </Typography>
              </Box>
            </Box>
          </Box>

          {transfers.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                No transfers recorded yet
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                    <TableCell>
                      <strong>Date</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Player</strong>
                    </TableCell>
                    <TableCell align="center">
                      <strong>Transfer</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Reason</strong>
                    </TableCell>
                    <TableCell>
                      <strong>By</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transfers.map((transfer, index) => (
                    <TableRow
                      key={`${transfer.playerId}-${index}`}
                      sx={{
                        bgcolor: getTransferColor(
                          transfer.fromDivision,
                          transfer.toDivision,
                        ),
                        "&:hover": {
                          bgcolor: "#f5f5f5",
                        },
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2">
                          {transfer.movedAt.toLocaleDateString("en-GB", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                          })}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                        >
                          {transfer.movedAt.toLocaleTimeString("en-GB", {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          {transfer.playerProfilePicture ? (
                            <Avatar
                              src={transfer.playerProfilePicture}
                              sx={{ width: 32, height: 32 }}
                            />
                          ) : (
                            <Avatar sx={{ width: 32, height: 32 }}>
                              {transfer.playerName.charAt(0)}
                            </Avatar>
                          )}
                          <Typography variant="body2">
                            {transfer.playerName}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            justifyContent: "center",
                          }}
                        >
                          <Chip
                            label={transfer.fromDivision}
                            size="small"
                            variant="outlined"
                          />
                          {getTransferIcon(
                            transfer.fromDivision,
                            transfer.toDivision,
                          )}
                          <Chip
                            label={transfer.toDivision}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        {transfer.reason ? (
                          <Tooltip title={transfer.reason} arrow>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <InfoIcon
                                sx={{ fontSize: 16, color: "#757575" }}
                              />
                              <Typography
                                variant="body2"
                                sx={{
                                  maxWidth: 250,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {transfer.reason}
                              </Typography>
                            </Box>
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            -
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={transfer.movedBy || "System"}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

export default Transfers;
